import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';

import apiGw from '@/services/apiGw';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { createResourceUri } from '@/utils/links';
import { PartnershipURI } from '@/types/partnership';
import { usePartnershipsStore } from '@/store/partnershipsStore';

export enum EntitlementType {
  Partnership = 'Partnership',
  PartnershipContract = 'PartnershipContract',
  CashAccount = 'CashAccount',
  CardIdentity = 'CardIdentity',
  CardProgram = 'CardProgram'
}

export enum EntitlementAction {
  Approve = 'approve',
  Capture = 'capture',
  View = 'view'
}

interface PartnershipEntitlement {
  entitlementUID: string;
  contractUID: string;
  contractType: string;
  entityUID: string;
  entityType: EntitlementType;
  empowerment: string;
  action: EntitlementAction;
  includedTypes: string[];
}

export const useEntitlementsStore = defineStore('entitlements', () => {
  const contextSwitchStore = useContextSwitchStore();
  // STATES
  const isLoading = ref(false);
  const entitlements = ref<PartnershipEntitlement[]>([]);

  // GETTERS
  function getEntitlements() {
    return entitlements.value;
  }

  // ACTIONS
  function setEntitlements(ent: PartnershipEntitlement[]) {
    entitlements.value = ent;
  }

  async function loadEntitlements(partnershipUid?: string) {
    isLoading.value = true;
    const entitlementsRes: AxiosResponse = await apiGw.post(
      createResourceUri(PartnershipURI.BaseV2, { uid: partnershipUid, resource: PartnershipURI.MyEntitlements })
    );
    setEntitlements(entitlementsRes.data.entitlements);
    isLoading.value = false;
  }

  function $reset() {
    entitlements.value = [];
  }

  function isEntitledToType(entityUID: string, action: EntitlementAction, type: EntitlementType): boolean {
    return entitlements.value.some(
      (ent: PartnershipEntitlement) =>
        ent.entityUID == entityUID &&
        ent.action === action &&
        (ent.includedTypes?.some((element) => element === type) ?? false)
    );
  }

  function isEntitledToEntity(action: EntitlementAction, entityUID: string): boolean {
    return entitlements.value.some(
      (ent: PartnershipEntitlement) => ent.action === action && ent.entityUID === entityUID
    );
  }

  function hasAnyRightToEntity(entityUID: string, type: EntitlementType): boolean {
    return entitlements.value.some(
      (ent: PartnershipEntitlement) => ent.entityUID === entityUID && ent.entityType === type
    );
  }

  function hasRightOnEntityInList(entityUIDList: string[], type: EntitlementType, action: EntitlementAction): boolean {
    return entitlements.value.some(
      (ent: PartnershipEntitlement) =>
        entityUIDList.includes(ent.entityUID) &&
        ent.action === action &&
        (ent.includedTypes?.some((element) => element === type) ?? false)
    );
  }

  function entitledAccounts(action: EntitlementAction): string[] {
    return entitlements.value
      .filter((ent: PartnershipEntitlement) => ent.entityType === EntitlementType.CashAccount && ent.action === action)
      .map((ent: PartnershipEntitlement) => ent.entityUID);
  }

  const hasCaptureAndApproveEntitlementToAnyAccount = computed((): boolean => {
    const cashAccountEntitlements: PartnershipEntitlement[] = entitlements.value.filter(
      (ent: PartnershipEntitlement) => ent.entityType === EntitlementType.CashAccount
    );

    const groupedByEntityUID: { [entityUID: string]: EntitlementAction[] } = {};
    cashAccountEntitlements.forEach((ent: PartnershipEntitlement) => {
      if (!groupedByEntityUID[ent.entityUID]) {
        groupedByEntityUID[ent.entityUID] = [];
      }
      groupedByEntityUID[ent.entityUID].push(ent.action);
    });

    return Object.values(groupedByEntityUID).some(
      (actions: EntitlementAction[]) =>
        actions.includes(EntitlementAction.Capture) && actions.includes(EntitlementAction.Approve)
    );
  });

  const hasCaptureEntitlementToAnyAccount = computed((): boolean => {
    const cashAccountEntitlements: PartnershipEntitlement[] = entitlements.value.filter(
      (ent: PartnershipEntitlement) => ent.entityType === EntitlementType.CashAccount
    );

    return cashAccountEntitlements.some((ent: PartnershipEntitlement) => ent.action === EntitlementAction.Capture);
  });

  watch(
    () => contextSwitchStore.currentContextUid,
    (newVal) => {
      const partnershipsStore = usePartnershipsStore();
      if (
        !contextSwitchStore.isPrivateContext &&
        contextSwitchStore.currentContextUid &&
        !partnershipsStore.isRequestorBootstrapperForCurrentPartnership
      ) {
        loadEntitlements(newVal);
      } else {
        $reset();
      }
    }
  );

  return {
    isLoading,
    entitlements,
    getEntitlements,
    setEntitlements,
    loadEntitlements,
    $reset,
    isEntitledToType,
    isEntitledToEntity,
    hasAnyRightToEntity,
    hasRightOnEntityInList,
    entitledAccounts,
    hasCaptureAndApproveEntitlementToAnyAccount,
    hasCaptureEntitlementToAnyAccount
  };
});
